import { fn, INamed } from '../utils';
import isSet = fn.isSet;
import { IPageProperty, IPageMetaData, NavDtFormat, TDefaultPageObj, ILookupInfo, TPagePropRefs } from '../nav/navtypes';
import { Model, Observable, ObservableArray, IObservableArray, GridColumn } from '../nav/navmodel';
import { NavClientService } from './navclientservice';
import { INavPageClientInfo_Data, IPageQueryParameters } from './navdata';
import { IPageQueryConstraint, TPagePropertyAccess } from '../nav/navtypes';
import { NavPropertyItem } from './navpropertyitem';
import { Bind } from '../navkendo/kutils';
export { Model, IObservableArray, ObservableArray, Observable, GridColumn }

/** Cancellable Event on Execute Update on PageItem (triggered on attached Model)*/
export const $PageItemExecuteUpate = "executeUpdate";
/** Event on Update Executed */
export const $PageItemExecutedUpdate = "executedUpdate";

/** transfer JSON  deserialized Objects Props in dest object */
function transfer<TDest, TSource>(dest: TDest, source: TSource) {
    for (var name in source)
        (<any>dest)[name] = source[name];
    return <TDest & TSource>dest;
}

/** Page Client Info from Server is JSON Object, internally we use a "real" object  */
export class NavPageClientInfo<TPage = TDefaultPageObj> /* implements INavPageClientInfo_Data */ {
    constructor(_data: INavPageClientInfo_Data<TPage>, public readonly Service: NavClientService, public readonly PageName: string, public readonly NavUrl: string) {
        let me = transfer(this, _data);
        var meta = me.MetaData = <IPageMetaData<TPage>>new NavPageMetaData(me.MetaData);
        meta.Properties.forEach(p => {
            if (p.CustomSearch) {
                this._customSearch[p.Name] = p.CustomSearch;
            }
            if (arrProps = p.ArrayProperties) {
                var arrProps = p.ArrayProperties = <IPageMetaData>new NavPageMetaData(arrProps);
                arrProps.Parent = meta;
                arrProps.ParentProperty = p;
                // Enforce "Key" as KeyField if not explicitly  defined
                // (not a good idea if we Edit Line Item in grid! )
                if (!arrProps.KeyFields || arrProps.KeyFields.length == 0)
                    arrProps.KeyFields = ["Key"];
            }
        });

        let allMeths = this.AllMethods = <INamed<boolean>>{};
        [].concat(me.ObjectMethods || []).concat(me.RowMethods || []).forEach(item => allMeths[item] = true);
    }

    //#region Client Side Custom Functionality

    /** Container of All Properties by their Path , attached  on Client after Retrieval! */
    readonly AllProperties: INamed<IPageProperty> = {};
    /** Container for Available Properties = true (ObjectMethods + RowMethods) */
    readonly AllMethods: INamed<boolean>;

    /** Resolve Search Optimization */
    public ResolveSearchOptimization(constraintInfo: IPageQueryParameters<TPage> & { NoResult?: boolean }): PromiseLike<IPageQueryParameters<TPage> & { NoResult?: boolean }> {
        const $c = this.Service.$c,
            allConstraints = constraintInfo && constraintInfo.Constraints,
            maxRead = 200,
            customConstraints = allConstraints && allConstraints.filter(item => this._customSearch[item.FieldName]);
        if (!customConstraints || customConstraints.length == 0) {
            return $c.when(constraintInfo);
        }
        const customConstraintsInfo = customConstraints.map(customConstraint => {
            var c = this._customSearch[customConstraint.FieldName];
            var mappedProp = this.AllProperties[c.LookupField];
            if (!(mappedProp && mappedProp.HasLookup))
                throw new Error("Fehlerhafte Konfiguration für optimierte Suchkonfiguration :" + ((mappedProp && mappedProp.Name) || (customConstraint.FieldName)));
            if (!c.LookupInfo) {
                c.LookupInfo = this.Service.GetStaticLookupInfo(this.NavUrl, this.PageName, mappedProp.Name);
            }
            return {
                Constraint: customConstraint,
                CustomSearch: c,
                MappedProp: mappedProp
            };
        });
        var customConstraintGroups = fn.DictToArray(fn.GroupBy(customConstraintsInfo, info => info.MappedProp.Name));

        const resolve = customConstraintGroups.map(constraintGroup => {
            const firstItem = constraintGroup[0];
            const lookupInfo = firstItem.CustomSearch.LookupInfo;
            return lookupInfo.then(d => {
                return this.Service.ReadMultiple({
                    PageName: d.TargetPageName,
                    PageCount: maxRead + 1,
                    Properties: [d.TargetField],
                    Constraints: constraintGroup.map(item => <IPageQueryConstraint>{
                        FieldName: item.CustomSearch.LookupProperty,
                        Op: item.Constraint.Op,
                        Value: item.Constraint.Value
                    })
                }, this.NavUrl).then(r => {
                    if (r.Data.length > maxRead) {
                        const currTagetField = this.AllProperties[firstItem.MappedProp.Name].Title;
                        throw new Error(`Bitte schränken Sie Ihre Abfrage weiter ein: Die Suche nach '${currTagetField}(${ constraintGroup.map(item => item.Constraint.FieldName).join(",") })' liefert mehr als '${maxRead}' Ergebnisse!`);
                    }
                    if (r.Data.length == 0) {
                        // no Result : add. Search not necessary anymore
                        constraintInfo.NoResult = true;
                    }
                    constraintGroup.forEach((item, currGroupIdx) => {
                        let idx = allConstraints.indexOf(item.Constraint);

                        var filterValue = r.Data.length > 0 ? r.Data.map(el => el[d.TargetField]).filter(isSet).join("|") : "<!NORESULT!>";
                        // only replace first, remove others
                        if (currGroupIdx == 0) {
                            allConstraints.splice(idx, 1, { FieldName: <any>item.CustomSearch.LookupField, Value: filterValue });
                        } else {
                            allConstraints.splice(idx, 1);
                        }
                    });
                });
            });
        });
        return $c.all(resolve).then(() => $c.when(constraintInfo));
    }

    private readonly _customSearch: INamed<{
        LookupField: string,
        LookupProperty: string,
        LookupInfo?: PromiseLike<ILookupInfo>;
    }> = <any>{};
    //#endregion
}
export type INavPageClientInfo<TPage = TDefaultPageObj, TPageName extends string = string> = NavPageClientInfo<TPage> & INavPageClientInfo_Data<TPage, TPageName>;

/** Page Client Info from Server is JSON Object, internally we use a "real" object
* so we can use it on kendo options object (as gets cloned internally will fail otherwise)
 */
export class NavPageMetaData<TPage = any> extends Observable /** implements _IPageMetaData<TPage> */ {
    constructor(meta: IPageMetaData<TPage>) {
        super();
        transfer(this, meta);
    }

    /** Global Register Property Changed Callback for any Instantiated Property Item from given Metadata Description */
    public RegisterPropertyChanged(this: IPageMetaData<TPage>, propRef: TPagePropertyAccess<TPage> | Array<TPagePropertyAccess<TPage>> | "*",
        callback: (ev: { Prop?: IPageProperty; Item: NavPropertyItem<TPage> }) => void) {
        return Bind(this, e => {
             const item = e.Item as NavPropertyItem;
             item.RegisterPropertyChanged(propRef, callback);
             callback({ Item: item });
        }, "Instantiate");
    }

    /** Register Custom Enum Invalidation  depending on other props */
    RegisterInvalidateEnum<TPage>(this: IPageMetaData<TPage>,
        sourceProp: TPagePropRefs<TPage>,
        targetPropertyName: keyof TPage, invalid: (model: TPage) => number[], reverse?: boolean) {
        const targetProp: IPageProperty = this.PropsByName[targetPropertyName];
        const enumValues = targetProp?.EnumMappings;
        if (enumValues) {
            return this.RegisterPropertyChanged(sourceProp, e => {
                const Item = e.Item;
                if (reverse) {
                    const all = enumValues.map(item => item.Value);
                    setInvalid = () => {
                        const valid = invalid(Item.Model) || [];
                        return all.filter(item => valid.indexOf(item) < 0);
                    };

                } else {
                    var setInvalid = () => invalid(Item.Model);
                }
                Item.GetCustomInvalidEnumData(targetProp).SetInvalid(setInvalid());
            });
        }
    }

    Instantiate(this: IPageMetaData<TPage>, item: NavPropertyItem<TPage>) {
        this.trigger("Instantiate", { Item: item });
    }
}

/** Default behaviour for failure of retrieving Props By name from MetaData, true : error, false : silently ignore them */
export const __ValidateProps = false;

/** Create Kendo Model from IPageProperty Fields Container */
export function CreateKendoModel(props: IPageProperty[]) {
    const fields = props.map(p => {
        if (p.ArrayProperties) {
            var arrType = getOrCreateKendoModel(p.ArrayProperties);
            var explicitParse = fn.identity;
        } else {
            var simpleType = getkendoModelType(p);
        }
        const result = <kendo.data.DataSourceSchemaModelField>{
            field: p.Name,
            editable: !p.ReadOnly,
            type: arrType || simpleType,
            nullable: true
        };

        if (explicitParse) {
            // hierarchical model parse not supported, this scenario leads to errors otherwise
            result.parse = explicitParse;
        }
        return result;
    });
    fields["fields"] = fields; // fields : TODO : if Set we might have to adjust filter (...)

    return Model.define(<any>fields);
}

/** Determine the kendo Model Type by MetaData Info (and attach single instance to Item) */
export function getOrCreateKendoModel(meta: IPageMetaData) {
    var kTypeAcess: { _kType: typeof Model } = <any>meta;
    return kTypeAcess._kType ? kTypeAcess._kType : (kTypeAcess._kType = CreateKendoModel(meta.Properties));
}

/** Get or Create Client Key determination func for given MetaData Instance */
export function getClientKeyFunc(metaData: IPageMetaData): (data: any, noError?: boolean) => string {

    /** Get a string Representation of a Key Identifier from Explicit Key Fields (unique for all Root/Line Items) */
    function getClientKey(this: IPageMetaData, data: any, noError?: boolean): string {
        if (!data) {
            if (noError) return "";
            throw new Error("Expected data");
        }
        data = data.Model || data;
        var keys = this.KeyFields;
        if (!keys || keys.length == 0) {
            if (noError) return "";
            throw new Error("KeyFields not defined for '" + this.PageName + "'" + (this.PropertyName || ""));
        }

        return keys.map(item => {
            var keyValue = data[item];
            if (keyValue == undefined) {
                if (noError) return "";
                throw new Error("Key Value for '" + item + "' not defined on item for MetaData :" + this.PageName + (this.PropertyName ? "[" + this.PropertyName + "]" : ""));
            }
            return item + ":" + keyValue;
        }).join("#") + "#" + this.PageName + "#" + (this.PropertyName || "");
    }

    return metaData["GetClientKey"] || (metaData["GetClientKey"] = getClientKey.bind(metaData));
}

/** set Value on Model (TODO : Do we want to Use kendo Model?) */
export function setModel(model: Model, name: string, value: any) {
    model[name] = value;
    //Remark : standard "kendo" Model "change notification" functionality via "set"
    // - all our custom controls do not need this functionality
    // - LineDisplay, i.e. Grid use to be handled via this, but :
    //      -  Change refreshes whole line then --> poor performance
    // --> so Handle Change  notification in Grid Display Explicitly, avoid setting via Notification ....

    // TODO : Invalidates Grid Data!, Set via function is necessary for Grid Display to refresh
    //model.set(name, value);
}

/** get Value on Model (TODO : Do we want to Use kendo Model?) */
export function getModel<TModel extends kendo.data.ObservableObject, TValue extends keyof TModel>(model: TModel, name: TValue): TModel[TValue] {
    return model[name];
}

/** Check if a Change occured on prepared Values for given Property */
export function valHasChanged(prop: IPageProperty, val1: any, val2: any) {
    switch (prop.Type) {
        case "DateTime":
            if (val1 instanceof Date && val2 instanceof Date) {
                var dt1 = (<Date>val1).valueOf();
                var dt2 = (<Date>val2).valueOf();
                if (dt1 == dt2)
                    return false;
                // > 1000 : DateTime gets transferred as string, with a more exact precision thang JScript Date can handle
                // Negation:  imp Handle nan
                return (isNaN(dt1) != isNaN(dt2)) || (Math.abs(dt1 - dt2) > 1000);
            } else {
                val1 = val1 === null ? new Date(NaN) : val1;
                val2 = val2 === null ? new Date(NaN) : val2;
                return (new Date(val1)).toString() != new Date(val2).toString();
            }
        default:
            if (prop.ArrayProperties) {
                // TODO (necessary?)
                return false;
                //prop.ArrayProperties.Properties.some(v =>
            }
            else {
                if (val1 === val2)
                    return false;
                return val1 != val2;
            }
    }
}

/** get Kendo Model Type by PageProperty Definition */
function getkendoModelType(p: IPageProperty): any {
    switch (p.Type) {
        case "DateTime":
            return p.DateFormat == NavDtFormat.Time ? "time" : "date";
        case "Decimal":
        case "Int32":
            return "number";
        case "Boolean":
            return "boolean";
        default:
            if (p.ArrayProperties)
                return getOrCreateKendoModel(p.ArrayProperties);
            else if (p.EnumMappings)
                return "number";
            else
                return "string";
    }
}

/** Enforce Typed for given Property by Value */
export function enforceTyped(prop: IPageProperty, value: any) {
    var origValue = value;
    var fail = (message: string) => { throw new Error("Error Setting Property '" + prop.Name + " from '" + origValue + "'': " + message); };
    switch (prop.Type) {
        case "String":
            if (typeof value != "string" && value != null && value != undefined)
                fail("Value must be string");
            return value;
        case "Int32":
            if (value == null || value == undefined)
                return value;
            value = (value === "") ? 0 : parseInt(value);
            if (isNaN(value))
                fail("Value must be int");
            return value;
        case "Decimal":
            if (value == null || value == undefined)
                return value;
            if(typeof value == "string") { // parsefloat is not locale aware!
                value = value.replace(/\./g, "").replace(",", ".");
            }
            value = value === "" ? 0.0 : parseFloat(value);
            if (isNaN(value))
                fail("Value must be float");
            return value;
        case "Boolean":
            if (fn.isObject(value))
                fail("Set from Object not possible!");
            if (value == "true")
                value = true;
            if (value == "false")
                value = false;
            return !!value;

        case "DateTime":
            if (value instanceof Date) {
                if (isNaN(value.valueOf()))
                    return null;

                if (prop.DateFormat == NavDtFormat.Time) {
                    // for "Time" props that's the Date used in NAV (1.1.0001)
                    value.setDate(1);
                    value.setMonth(0);
                    value.setFullYear(1);
                }
                return value;
            }
            if (value == null || value == "" || value == undefined)
                return null;
            if (typeof value === "string")
                value = fn.ToDate(value);
            if (!isNaN(value))
                value = new Date(value);

            if (!fn.IsDate(value))
                fail(" Must be Date");
            return value;
        default:
            if (prop.EnumMappings) {
                if (typeof value == "string") {
                    var intVal;
                    if ((intVal = parseInt(value)) >= 0)
                        value = intVal;
                    else if (!prop.EnumMappings.some(m => { if (m.Title == value || m.Value.toString() == value) { value = m.Value; return true; } return false; }))
                        fail("Invalid Enum Mapping Value!");
                }

                if (typeof value == "number")
                    if (!prop.EnumMappings.some(item => item.Value == value))
                        fail("Invalid Enum Value!");

                return value;
            }
            else {
                throw new Error(`TODO : Implement enforceTyped for Type ${prop.Type}!`);
            }
    }
}
